import {
  Box,
  Dialog,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { tokens } from '../../global/theme/tokens';
import Header from '../../components/special/Header';
import { useTranslation } from 'react-i18next';
import { ProjectsGrid } from './ProjectsGrid';
import { useEffect, useState } from 'react';
import ClassIcon from '@mui/icons-material/Class';
import WindowIcon from '@mui/icons-material/Window';
import TableRowsIcon from '@mui/icons-material/TableRows';
import { ExoProjectCategory } from './category/ExoProjectCategory';
import { ProjectCategoriesView } from './project-categories/ProjectCategoriesView';
import { QuickAccessProjects } from './quick-access/QuickAccessProjects';
import { DBRequest, useConfig } from '../../api/api';
import ExoAPIFilter from '../../components/exo/ExoAPIFilter';
import Cookies from 'universal-cookie';

const glassyButtonSx = {
  border: 1,
  borderColor: 'divider',
  borderRadius: '4px',
};

const CATEGORY_FILTER_COOKIE = 'projects-category-filter';
const PROJECTS_VIEW_STATE_KEY = 'projects-view-state';

// --- pure helpers -----------------------------------------------------------

function readProjectsViewState() {
  try {
    return JSON.parse(localStorage.getItem(PROJECTS_VIEW_STATE_KEY) || '{}');
  } catch {
    return {};
  }
}

// Category ids currently encoded in the filter string (e.g. "categoryIds[]=3&").
function getSelectedCategoryIds(filterString = '') {
  return (filterString.match(/categoryIds\[\]=([^&]+)/g) || []).map((match) =>
    decodeURIComponent(match.split('=')[1])
  );
}

function isUncategorizedFilter(filterString = '') {
  return filterString.startsWith('uncategorized=true');
}

// Look up a category by id across the given lists, first match wins.
function findCategoryById(id, ...lists) {
  for (const list of lists) {
    const match = (list || []).find((c) => String(c.id) === String(id));
    if (match) return match;
  }
  return null;
}

// --- presentational components ----------------------------------------------

function GlassLabel({ colors, className = '', ...props }) {
  return (
    <Typography
      variant="h4"
      className={`w-fit px-3 py-1 rounded-lg backdrop-blur-lg opacity-90 ${className}`}
      sx={{ bgcolor: colors.glass, ml: { xs: 1, md: 0 } }}
      {...props}
    />
  );
}

function CategoryIcon({ category }) {
  if (category.icon?.temporaryUrl)
    return (
      <img
        alt=""
        src={category.icon.temporaryUrl}
        className="h-5 w-5 object-contain"
      />
    );
  if (category.VirtualIcon)
    return <category.VirtualIcon sx={{ color: category.color }} />;
  return <ClassIcon sx={{ color: category.color }} />;
}

function CategoryBreadcrumb({ crumb, category, colors, onReset }) {
  const { t } = useTranslation();
  return (
    <Box
      className="w-fit flex flex-row items-center gap-2 my-2 px-3 py-1 rounded-lg backdrop-blur-lg"
      sx={{ bgcolor: colors.glass, ml: { xs: 1, md: 0 } }}
    >
      <Typography
        variant="h4"
        onClick={crumb ? onReset : undefined}
        className={
          crumb
            ? 'opacity-90 cursor-pointer transition-opacity hover:opacity-100'
            : 'opacity-90'
        }
      >
        {t('Categories')}
      </Typography>

      {crumb && (
        <>
          <Typography variant="h4" className="opacity-60">
            ›
          </Typography>
          {category ? (
            <Box className="flex flex-row items-center gap-2">
              <Box
                className="h-8 w-8 rounded-full flex items-center justify-center shrink-0"
                sx={{ bgcolor: (category.color || '') + '30' }}
              >
                <CategoryIcon category={category} />
              </Box>
              <Typography variant="h4" className="opacity-90">
                {crumb}
              </Typography>
            </Box>
          ) : (
            <Typography variant="h4" className="opacity-90">
              {crumb}
            </Typography>
          )}
        </>
      )}
    </Box>
  );
}

// --- scene ------------------------------------------------------------------

const Projects = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();
  const config = useConfig();
  const cookies = new Cookies();

  const [projectStatus, setProjectStatus] = useState(
    () => readProjectsViewState().projectStatus || 'own'
  );
  const [viewMode, setViewMode] = useState(
    () => readProjectsViewState().viewMode || 'grid'
  );
  const [showAll, setShowAll] = useState(() =>
    Boolean(readProjectsViewState().showAll)
  );

  const [openCategories, setOpenCategories] = useState(false);
  const [categories, setCategories] = useState(null);
  const [recentProjects, setRecentProjects] = useState(null);

  const [currentFilter, setCurrentFilter] = useState({
    string: cookies.get(CATEGORY_FILTER_COOKIE) || '',
  });

  useEffect(() => {
    localStorage.setItem(
      PROJECTS_VIEW_STATE_KEY,
      JSON.stringify({ projectStatus, viewMode, showAll })
    );
  }, [projectStatus, viewMode, showAll]);

  useEffect(() => {
    DBRequest({
      config,
      path: `project-categories`,
      onResponse: setCategories,
    });
  }, [config]);

  useEffect(() => {
    DBRequest({
      config,
      path: `projects/own?status[eq]=0&perPage=4`,
      onResponse: setRecentProjects,
    });
  }, [config]);

  // --- event handlers -------------------------------------------------------

  function handleViewModeChange(_, value) {
    if (!value) return;
    setViewMode(value);
  }

  function handleFilterChange(changedFilter) {
    const newString = changedFilter?.string || '';
    if (!newString) setShowAll(false);
    setCurrentFilter({ ...changedFilter, string: newString });
  }

  function handleCategorySelect(category) {
    const filterItem = {
      type: 'projectCategory',
      label: t('Category'),
      key: 'categoryIds',
      baseValue: 'categoryIds[]=',
      active: true,
      value: `categoryIds[]=${category.id}`,
      dataCollection: { category: [category] },
    };
    const string = `${filterItem.value}&`;
    cookies.set(CATEGORY_FILTER_COOKIE, string, { path: '/' });
    sessionStorage.setItem(CATEGORY_FILTER_COOKIE, JSON.stringify([filterItem]));
    setShowAll(false);
    setCurrentFilter({ filters: [filterItem], order: {}, string });
  }

  function handleSelectAll() {
    cookies.remove(CATEGORY_FILTER_COOKIE, { path: '/' });
    sessionStorage.removeItem(CATEGORY_FILTER_COOKIE);
    setShowAll(true);
    setCurrentFilter({ string: '' });
  }

  function handleSelectUncategorized() {
    const string = 'uncategorized=true&';
    cookies.set(CATEGORY_FILTER_COOKIE, string, { path: '/' });
    sessionStorage.removeItem(CATEGORY_FILTER_COOKIE);
    setShowAll(false);
    setCurrentFilter({ string });
  }

  function handleResetCategories() {
    cookies.remove(CATEGORY_FILTER_COOKIE, { path: '/' });
    sessionStorage.removeItem(CATEGORY_FILTER_COOKIE);
    setShowAll(false);
    setCurrentFilter({ string: '' });
  }

  // --- derived view state ---------------------------------------------------

  const filters = {
    cookieKey: CATEGORY_FILTER_COOKIE,
    filter: {
      label: t('Filter'),
      items: [
        {
          type: 'projectCategory',
          label: t('Category'),
          key: 'categoryIds',
          baseValue: 'categoryIds[]=',
          active: false,
        },
      ],
    },
  };

  const hasCategories = (categories?.data?.length || 0) > 0;
  const showCategories = !showAll && !currentFilter.string && hasCategories;

  const selectedCategoryIds = getSelectedCategoryIds(currentFilter.string);
  const isUncategorized = isUncategorizedFilter(currentFilter.string);

  const activeFilterCategories =
    (currentFilter.filters || []).find(
      (f) => f.key === 'categoryIds' && f.active
    )?.dataCollection?.category || [];

  // The single selected category (only relevant when exactly one is filtered).
  let selectedCategory = null;
  let categoryCrumb = null;
  if (showAll) {
    categoryCrumb = t('All');
  } else if (isUncategorized) {
    categoryCrumb = t('Uncategorized');
  } else if (selectedCategoryIds.length === 1) {
    selectedCategory = findCategoryById(
      selectedCategoryIds[0],
      activeFilterCategories,
      categories?.data
    );
    categoryCrumb = selectedCategory?.label || t('Category');
  } else if (selectedCategoryIds.length > 1) {
    categoryCrumb = t('Multiple Categories');
  }

  // Heading shown above the project list / category overview.
  let heading = null;
  if (showCategories || categoryCrumb) {
    heading = (
      <CategoryBreadcrumb
        crumb={categoryCrumb}
        category={selectedCategory}
        colors={colors}
        onReset={handleResetCategories}
      />
    );
  } else if (projectStatus === 'own') {
    heading = (
      <GlassLabel colors={colors} className="my-2">
        {t('All')}
      </GlassLabel>
    );
  }

  // View-mode / category / filter controls, shown inline with the heading.
  const viewControls = (
    <Box className="flex gap-2 items-center" sx={{ px: { xs: 1, md: 0 } }}>
      <ToggleButtonGroup
        size="small"
        value={viewMode}
        exclusive
        onChange={handleViewModeChange}
        aria-label="View Mode"
        className="backdrop-blur-lg"
      >
        <ToggleButton value="grid" aria-label={t('Grid view')}>
          <Tooltip title={t('Grid view')}>
            <WindowIcon fontSize="small" />
          </Tooltip>
        </ToggleButton>
        <ToggleButton value="list" aria-label={t('List view')}>
          <Tooltip title={t('List view')}>
            <TableRowsIcon fontSize="small" />
          </Tooltip>
        </ToggleButton>
      </ToggleButtonGroup>
      <Tooltip title={t('Category')}>
        <IconButton
          size="small"
          onClick={() => setOpenCategories(true)}
          className="backdrop-blur-lg"
          sx={glassyButtonSx}
        >
          <ClassIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <ExoAPIFilter
        filters={filters}
        onChange={handleFilterChange}
        currentFilters={currentFilter}
        smallIcon={true}
        buttonClassName="backdrop-blur-lg"
        buttonSx={glassyButtonSx}
        isActive={showAll}
      />
    </Box>
  );

  const topContent = (
    <>
      {projectStatus === 'own' && (
        <>
          <GlassLabel colors={colors} className="mb-2">
            {t('Recent')}
          </GlassLabel>
          <QuickAccessProjects recentProjects={recentProjects} />
        </>
      )}
      <Box className="flex flex-row items-center justify-between gap-2 flex-wrap pr-2">
        <Box>{heading}</Box>
        {viewControls}
      </Box>
    </>
  );

  // --- render ---------------------------------------------------------------

  return (
    <>
      <Box className="flex gap-0 md:gap-8 md:items-center justify-between md:justify-start pr-2 md:flex-row flex-col pb-4 md:pb-0">
        <Header title={t('Projects')} />
        <Box className="flex gap-2 items-center" sx={{ px: { xs: 1, md: 0 } }}>
          <ToggleButtonGroup
            size="small"
            value={projectStatus}
            exclusive
            onChange={(e) => setProjectStatus(e.target.value)}
            aria-label="Project Status"
            className="backdrop-blur-lg"
          >
            <ToggleButton value="own">{t('Own')}</ToggleButton>
            <ToggleButton value="active">{t('Active')}</ToggleButton>
            <ToggleButton value="archive">{t('Archived')}</ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Box>

      {showCategories ? (
        <ProjectCategoriesView
          categories={categories}
          viewMode={viewMode}
          onSelect={handleCategorySelect}
          onSelectAll={handleSelectAll}
          onSelectUncategorized={handleSelectUncategorized}
          topContent={topContent}
        />
      ) : (
        <ProjectsGrid
          projectStatus={projectStatus}
          filterString={currentFilter.string}
          viewMode={viewMode}
          hideCreate={projectStatus === 'own'}
          topContent={topContent}
        />
      )}

      <Dialog
        open={openCategories}
        onClose={() => setOpenCategories(false)}
        sx={{
          '.MuiPaper-root': {
            backgroundImage: 'unset!important',
          },
          '.MuiBackdrop-root': {
            backdropFilter: 'blur(1px)',
            backgroundColor: 'rgba(0, 0, 0, 0.3 )',
          },
        }}
      >
        <ExoProjectCategory
          onClose={() => setOpenCategories(false)}
          categories={categories}
          setCategories={setCategories}
        />
      </Dialog>
    </>
  );
};

export default Projects;
